import { Box, Flex } from "@chakra-ui/react";
import { HorizontalGreyBlackField } from "components/DataDisplay/GreyBlackField";
import { longAttributeStyles } from "components/DataDisplay/styles";
import MeetingAttendeeTag, { MeetingAttendeeAvatar } from "components/Tag/ResourceTargetTag/MeetingAttendeeTag";
import TwoTierPopoverTagColumn from "components/Column/TwoTierPopoverTagDisplay";
import { PlusNTag } from "components/Tag/PlusNTag";
import { useCallback, useMemo } from "react";
import { Meeting, MeetingAttendee } from "../models";

function attendeesText(attendees: MeetingAttendee[], includePlus: boolean = false) {
  return (
    `${includePlus ? "+" : ""}${attendees.length} Attendees`
  );
}

export function DisplayAttendeesValue({ meeting, includePlus = false }: { meeting: Meeting, includePlus?: boolean }) {
  return attendeesText(meeting?.attendees, includePlus);
}

const tagForAttendee = (attendee: MeetingAttendee) => (
  <MeetingAttendeeTag
    key={attendee.id}
    attendee={attendee}
    disabled
    responseStatus={attendee.responseStatus}
    role={attendee.role}
    isLeader={attendee.isLead}
  />
);

export function DisplayAttendeesColumn({
  attendees,
  allSessionAttendees,
}: {
  attendees?: MeetingAttendee[];
  allSessionAttendees?: MeetingAttendee[];
}) {
  // Always use meeting-specific attendees for avatar display
  const displayAttendees = useMemo(() => attendees || [], [attendees]);

  // Calculate other attendees (from allSessionAttendees but not in this meeting's attendees)
  const otherAttendees = useMemo(() => {
    if (!allSessionAttendees || allSessionAttendees.length === 0) return [];
    if (!attendees || attendees.length === 0) return allSessionAttendees;

    const attendeeUserIds = new Set(attendees.map((a) => a.userId));
    return allSessionAttendees.filter((a) => !attendeeUserIds.has(a.userId));
  }, [allSessionAttendees, attendees]);

  const maxAvatarCount = 3;

  const displaySummary = useCallback(() => (
    <Flex align="center" mx="2" flexDirection="row-reverse">
      <PlusNTag
        maxCount={maxAvatarCount}
        items={displayAttendees}
        ml="-6px"
        size="33px"
      />
      {displayAttendees.slice(0, maxAvatarCount).reverse().map((attendee) => (
        <Box key={attendee.id} ml="-6px">
          <MeetingAttendeeAvatar attendee={attendee} />
        </Box>
      ))}
    </Flex>
  ), [displayAttendees]);

  return (
    <Flex align="center" gap="1">
      <TwoTierPopoverTagColumn
        primaryItems={displayAttendees}
        secondaryItems={otherAttendees}
        renderTag={tagForAttendee}
        displaySummary={displaySummary}
        emptyLabel="No Attendees"
        secondaryLabel="Other Attendees"
      />
    </Flex>
  );
}

export function DisplayAllAttendees({ attendees, leadId }: { attendees?: MeetingAttendee[], leadId?: string }) {
  if (!attendees?.length) {
    return null;
  }

  return (
    <Flex align="center" gap="1">
      {attendees.map((attendee) => (
        <MeetingAttendeeTag
          key={attendee.id}
          attendee={attendee}
          disabled
          isLeader={leadId === attendee.id}
          responseStatus={attendee.responseStatus}
          role={attendee.role}
        />
      ))}
    </Flex>
  );
}

interface ShowAttendeesProps {
  attendees: MeetingAttendee[];
  leadId?: string;
  label: string;
}

export function ShowAttendees({ attendees, label, leadId, ...rest }: ShowAttendeesProps) {
  return (
    <HorizontalGreyBlackField
      label={label}
      labelContainerProps={longAttributeStyles.labelContainerProps}
      {...rest}
    >
      {!attendees.length ? (
        "None"
      ) : (
        <DisplayAllAttendees attendees={attendees} leadId={leadId} />
      )}
    </HorizontalGreyBlackField>
  );
}
